import React, { useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import api from 'api/api'
import cn from 'classnames'
import { AlertCircle, ChevronRight, CornerUpLeft, LockKeyhole } from 'lucide-react'
import answerModel from 'components/answers/answerModel'
import Perms from 'components/quests/Perms'
import PermsOptions from 'components/quests/PermsOptions'
import UserIcon from 'components/users/UserIcon'
import perms from 'lib/perms'
import questModel from 'components/quests/questModel'
import styles from './thin-parent.module.scss'
import { getParentPayload, normalizeApiUrl } from './crumbPayload'
import { cacheQuest, getCachedAnswer, getCachedQuest } from 'state/cache'
import usePanelContext from 'refactor/hooks/usePanelContext'
import useOpenRight from 'refactor/hooks/useOpenRight'
import {
    getThinParentQuestState,
    resolveQuestParentQuestId,
    resolveThinParentState,
} from 'state/questAncestors'
import type { ParentQuestFetchState, QuestWithAncestors } from 'state/questAncestors'
import type { Answer } from 'types/answers'
import type { Quest } from 'types/quests'
import { canPublishFromQuestBreadcrumb } from 'lib/questBreadcrumb'
import { QUEST_STREAM_CRUMB_CHROME_PX } from 'lib/staticGeometryChrome'
import { isDirectMessageBranch } from 'lib/directMessages'
import DirectMessageBranchCrumb from './DirectMessageBranchCrumb'
import AncestorCrumbs from './AncestorCrumbs'
import { ancestorCrumbTarget } from 'lib/ancestorCrumbs'
import { fixedHeaderEnabled } from 'lib/fixedHeader'
import { isMobile } from 'lib/device'

interface ThinParentProps {
    quest?: Quest
    isBsv?: boolean
    // The ancestor spine (scroll_ancestors) supersedes the one-level parent
    // crumb; the stream pill row stays either way.
    hideAncestorCrumb?: boolean
    // Pointer enter/leave over the strip. A stream card feeds it into its head
    // post's hover state (Quest.onCrumbHover).
    onHoverChange?: (hovered: boolean) => void
    // A click on the strip outside the pills. A stream card passes its head
    // post's click so the strip opens the thread like the rest of the card; the
    // pills stop propagation and keep their own destinations.
    onRowClick?: (ev: React.MouseEvent) => void
    // fixed_header, open thread: the panel's place bar shows the same crumb, so
    // the strip is dropped unless it carries the owner's publish control.
    redundantWithHeader?: boolean
    // A stream card (not the open thread).
    isCard?: boolean
}

interface PermsOptionsPosition {
    top: number
    left: number
    width: number
    maxHeight: number
}

type DisplayParentQuest =
    | NonNullable<ReturnType<typeof getThinParentQuestState>['grandParentQuest']>
    | QuestWithAncestors
    | Quest

const StreamThinParent: React.FC<ThinParentProps> = ({
    quest,
    isBsv,
    hideAncestorCrumb,
    onHoverChange,
    onRowClick,
    redundantWithHeader,
    isCard,
}) => {
    const { panel } = usePanelContext()
    const { openRight } = useOpenRight(panel.panelId)
    const thinParentQuestState = useMemo(
        () =>
            getThinParentQuestState({
                quest,
                getAnswerById: answerId => getCachedAnswer(String(answerId)),
            }),
        [quest],
    )

    const parentId = thinParentQuestState.parentId
    const inlineGrandParentQuest = thinParentQuestState.grandParentQuest
    const cachedGrandParentQuest = inlineGrandParentQuest?.id
        ? getCachedQuest(String(inlineGrandParentQuest.id))
        : null
    const usableGrandParentQuest = cachedGrandParentQuest || inlineGrandParentQuest || null
    const initialParentQuestId = thinParentQuestState.parentQuestId
    const initialParentQuest =
        usableGrandParentQuest ||
        (initialParentQuestId ? getCachedQuest(initialParentQuestId) : null)

    const [resolvedParentQuest, setResolvedParentQuest] = useState<DisplayParentQuest | null>(
        () => initialParentQuest,
    )
    const [resolvedParentQuestId, setResolvedParentQuestId] = useState<string | null>(
        () => initialParentQuestId,
    )
    const [parentQuestFetchState, setParentQuestFetchState] = useState<ParentQuestFetchState>(
        () => {
            if (!parentId && !initialParentQuestId) return 'missing'
            return initialParentQuest?.id || thinParentQuestState.grandParent?.id
                ? 'available'
                : 'loading'
        },
    )

    useEffect(() => {
        let cancelled = false

        async function loadParentQuest() {
            if (!parentId && !initialParentQuestId && !usableGrandParentQuest?.id) {
                setResolvedParentQuest(null)
                setResolvedParentQuestId(null)
                setParentQuestFetchState('missing')
                return
            }

            let nextParentQuestId = resolveQuestParentQuestId({
                questId:
                    thinParentQuestState.quest?.id != null
                        ? String(thinParentQuestState.quest.id)
                        : null,
                parentQuestId: initialParentQuestId,
                parentId,
                parentAnswer: thinParentQuestState.quest?.parent || null,
                getAnswerById: answerId => getCachedAnswer(String(answerId)),
            })

            if (nextParentQuestId) {
                setResolvedParentQuestId(nextParentQuestId)
            }

            const cachedParentQuest =
                usableGrandParentQuest ||
                (nextParentQuestId ? getCachedQuest(String(nextParentQuestId)) : null)
            if (cachedParentQuest?.id) {
                setResolvedParentQuest(cachedParentQuest)
                setParentQuestFetchState('available')
                if (
                    thinParentQuestState.grandParent?.id ||
                    cachedParentQuest.parent?.id ||
                    (cachedParentQuest.parent_id &&
                        getCachedAnswer(String(cachedParentQuest.parent_id))?.id)
                ) {
                    return
                }
            } else {
                setResolvedParentQuest(null)
            }

            if (!nextParentQuestId) {
                setResolvedParentQuest(null)
                setResolvedParentQuestId(null)
                setParentQuestFetchState(parentId ? 'unavailable' : 'missing')
                return
            }

            const latestCachedParentQuest = getCachedQuest(nextParentQuestId)
            if (latestCachedParentQuest?.id) {
                setResolvedParentQuest(latestCachedParentQuest)
                setParentQuestFetchState('available')
                if (
                    thinParentQuestState.grandParent?.id ||
                    latestCachedParentQuest.parent?.id ||
                    (latestCachedParentQuest.parent_id &&
                        getCachedAnswer(String(latestCachedParentQuest.parent_id))?.id)
                ) {
                    return
                }
            } else {
                setResolvedParentQuest(null)
            }

            setParentQuestFetchState('loading')
            const fetchedParentQuest = await api.getQuestQuery(nextParentQuestId)
            if (cancelled) return

            if (fetchedParentQuest?.id === '__PRIVATE__') {
                setResolvedParentQuest(null)
                setParentQuestFetchState('private')
                return
            }

            if (fetchedParentQuest?.id) {
                const cachedFetchedParentQuest = cacheQuest(fetchedParentQuest)
                setResolvedParentQuest(cachedFetchedParentQuest || fetchedParentQuest)
                setParentQuestFetchState('available')
                return
            }

            setResolvedParentQuest(null)
            setParentQuestFetchState('missing')
        }

        loadParentQuest()

        return () => {
            cancelled = true
        }
    }, [
        initialParentQuestId,
        parentId,
        thinParentQuestState.grandParent?.id,
        thinParentQuestState.quest?.id,
        thinParentQuestState.quest?.parent,
        usableGrandParentQuest?.id,
    ])

    const thinParentState = useMemo(
        () =>
            resolveThinParentState({
                quest: {
                    ...(thinParentQuestState.quest || {}),
                    parent_quest_id:
                        resolvedParentQuestId ||
                        thinParentQuestState.quest?.parent_quest_id ||
                        null,
                },
                parentQuest: (resolvedParentQuest as QuestWithAncestors | null) || null,
                parentQuestFetchState,
                getAnswerById: answerId => getCachedAnswer(String(answerId)),
            }),
        [
            parentQuestFetchState,
            resolvedParentQuest,
            resolvedParentQuestId,
            thinParentQuestState.quest,
        ],
    )

    const grandParentAnswer = thinParentState.displayGrandParent as Answer | null
    const grandParentId = thinParentState.displayGrandParentId
    const canOpenAncestorAnswer = !!thinParentState.parentQuestId && !!(grandParentId || parentId)
    const shouldShowParentBreadcrumb =
        !hideAncestorCrumb &&
        !!thinParentState.parentQuestId &&
        (!!grandParentAnswer?.id || thinParentState.shouldRenderUnavailablePreview)

    // The publish dropdown only exists on non-public roots the viewer owns: branches re-home
    // through their parent thread, and public/inscribed quests have nothing left to publish.
    const canPublish = canPublishFromQuestBreadcrumb({
        quest,
        parentQuestId: thinParentState.parentQuestId,
        isPublic: quest ? questModel.isPublic(quest) : false,
        isBsv: !!isBsv,
        isOwner: !!quest && perms.amQuestOwner(quest),
    })

    const crumbRef = useRef<HTMLDivElement | null>(null)
    const [permsOptionsPosition, setPermsOptionsPosition] = useState<PermsOptionsPosition | null>(
        null,
    )

    const closePermsOptions = () => setPermsOptionsPosition(null)

    const openPermsOptions = () => {
        const crumbEl = crumbRef.current
        if (!crumbEl || typeof window === 'undefined') return

        const rect = crumbEl.getBoundingClientRect()
        const viewportPadding = 12
        const width = Math.min(300, window.innerWidth - viewportPadding * 2)
        const left = Math.min(
            Math.max(viewportPadding, rect.left),
            Math.max(viewportPadding, window.innerWidth - viewportPadding - width),
        )
        const top = Math.min(rect.bottom + 4, Math.max(viewportPadding, window.innerHeight - 220))
        const maxHeight = Math.max(180, window.innerHeight - top - viewportPadding)

        setPermsOptionsPosition({ top, left, width, maxHeight })
    }

    const onClickAncestorAnswer = async () => {
        if (!thinParentState.parentQuestId || !(grandParentId || parentId)) return
        await openRight({
            questId: thinParentState.parentQuestId,
            answerId: grandParentId || parentId,
        })
    }

    const onClickStream = (ev: React.MouseEvent) => {
        ev.preventDefault()
        ev.stopPropagation()

        const targetFilter = quest ? questModel.getFilter(quest) : null
        if (!targetFilter) return

        openRight(targetFilter)
    }

    const parentText =
        answerModel.getText(grandParentAnswer) ||
        (!grandParentAnswer?.url?.tweet_id && grandParentAnswer?.url?.title) ||
        grandParentAnswer?.url?.address ||
        thinParentState.unavailablePreviewLabel
    const parentPayload = useMemo(() => getParentPayload(grandParentAnswer), [grandParentAnswer])
    const FallbackIcon = canOpenAncestorAnswer
        ? CornerUpLeft
        : thinParentState.isLockedPreview
        ? LockKeyhole
        : AlertCircle
    // Normal stream and hydrated-parent crumbs are one line and must stay identical to the
    // static row reserve. Unavailable previews may need their explanatory second line, so keep
    // the shared value as a minimum and let the idle measurement backstop that exceptional case.
    const thinParentGeometryStyle =
        shouldShowParentBreadcrumb && !grandParentAnswer?.id
            ? { minHeight: QUEST_STREAM_CRUMB_CHROME_PX }
            : { height: QUEST_STREAM_CRUMB_CHROME_PX }

    // fixed_header: the panel header's channel crumb carries the publish dropdown.
    if (redundantWithHeader) return null

    // fixed_header, a card in a channel's stream: the channel is already named
    // in the panel header, so the strip shows only the posts above the thread
    // (avatar + first words, deep chains folded into a count). A root thread
    // has none, and its card has no strip (questStreamHeight drops the reserve
    // to match).
    const channelCard = isCard && fixedHeaderEnabled() && panel?.filter?.team_ids?.length === 1
    if (channelCard) {
        if (!thinParentState.parentQuestId) return null
        const ancestors: Answer[] = Array.isArray((quest as any)?.ancestors)
            ? (quest as any).ancestors
            : grandParentAnswer
            ? [grandParentAnswer]
            : []
        return (
            <div
                className={cn(
                    styles.thinParentComp,
                    styles.channelCrumbRow,
                    onRowClick && styles.rowNavigable,
                )}
                style={{ height: QUEST_STREAM_CRUMB_CHROME_PX }}
                data-answer-breadcrumb="true"
                onMouseEnter={() => onHoverChange?.(true)}
                onMouseLeave={() => onHoverChange?.(false)}
                onClick={onRowClick}
            >
                <AncestorCrumbs
                    compact={isMobile}
                    ancestors={ancestors}
                    onOpen={i => {
                        const target = ancestorCrumbTarget(
                            ancestors,
                            i,
                            thinParentState.parentQuestId,
                            (quest as any)?.parent_id,
                        )
                        if (target) openRight(target)
                    }}
                />
            </div>
        )
    }

    // The publish dropdown is a sibling of the strip, not a child: React computes
    // mouse enter/leave over its own tree, portals included, so a portaled
    // backdrop inside the strip would keep it "hovered" for as long as the
    // dropdown is open and never deliver the leave once it closes.
    return (
        <>
            <div
                className={cn(styles.thinParentComp, onRowClick && styles.rowNavigable)}
                style={thinParentGeometryStyle}
                data-answer-breadcrumb="true"
                onMouseEnter={() => onHoverChange?.(true)}
                onMouseLeave={() => onHoverChange?.(false)}
                onClick={onRowClick}
            >
                <div
                    ref={crumbRef}
                    className={cn(
                        styles.streamCrumb,
                        !shouldShowParentBreadcrumb && styles.streamCrumbFull,
                    )}
                    onClick={onClickStream}
                >
                    <Perms
                        quest={quest}
                        isBsv={isBsv}
                        isCrumb={true}
                        disablePermsControl={!canPublish}
                        showPermsOptions={!!permsOptionsPosition}
                        setShowPermsOptions={(open: boolean) =>
                            open ? openPermsOptions() : closePermsOptions()
                        }
                        contextStyles={styles.streamPerms}
                    />
                </div>

                {shouldShowParentBreadcrumb && (
                    <>
                        <span className={styles.breadcrumbChevron} aria-hidden="true">
                            <ChevronRight size={15} strokeWidth={2.75} />
                        </span>

                        <button
                            type="button"
                            className={styles.parentCrumbButton}
                            disabled={!canOpenAncestorAnswer}
                            onClick={ev => {
                                ev.preventDefault()
                                ev.stopPropagation()
                                onClickAncestorAnswer()
                            }}
                            title={parentText}
                        >
                            {grandParentAnswer?.id ? (
                                <>
                                    {grandParentAnswer.user && (
                                        <span className={styles.parentUserIconContainer}>
                                            <UserIcon
                                                user={grandParentAnswer.user}
                                                size="small"
                                                contextStyles={styles.parentUserIcon}
                                            />
                                        </span>
                                    )}
                                    {parentPayload && (
                                        <span
                                            className={styles.payloadContainer}
                                            aria-hidden="true"
                                        >
                                            {parentPayload.kind === 'image' &&
                                            parentPayload.previewUrl ? (
                                                <img
                                                    src={normalizeApiUrl(parentPayload.previewUrl)}
                                                    alt=""
                                                    className={styles.payloadPreview}
                                                />
                                            ) : 'previewUrl' in parentPayload &&
                                              parentPayload.previewUrl ? (
                                                <img
                                                    src={normalizeApiUrl(parentPayload.previewUrl)}
                                                    alt=""
                                                    className={styles.payloadPreview}
                                                />
                                            ) : (
                                                <span className={styles.payloadIconContainer}>
                                                    {parentPayload.kind !== 'image' && (
                                                        <parentPayload.icon size={14} />
                                                    )}
                                                </span>
                                            )}
                                        </span>
                                    )}
                                    <span className={styles.parentCrumbText}>{parentText}</span>
                                </>
                            ) : (
                                <>
                                    <span className={styles.payloadContainer} aria-hidden="true">
                                        <span className={styles.payloadIconContainer}>
                                            <FallbackIcon size={14} />
                                        </span>
                                    </span>
                                    <span className={styles.parentCrumbMeta}>
                                        <span className={styles.parentCrumbText}>{parentText}</span>
                                        <span className={styles.parentCrumbSubtext}>
                                            {thinParentState.unavailablePreviewSubtext}
                                        </span>
                                    </span>
                                </>
                            )}
                        </button>
                    </>
                )}
            </div>

            {permsOptionsPosition &&
                typeof document !== 'undefined' &&
                createPortal(
                    <div
                        className={styles.permsOptionsBackdrop}
                        onClick={ev => {
                            ev.stopPropagation()
                            closePermsOptions()
                        }}
                    >
                        <div
                            className={styles.permsOptionsPopover}
                            style={{
                                top: `${permsOptionsPosition.top}px`,
                                left: `${permsOptionsPosition.left}px`,
                                width: `${permsOptionsPosition.width}px`,
                                maxHeight: `${permsOptionsPosition.maxHeight}px`,
                            }}
                            onMouseDown={ev => ev.stopPropagation()}
                            onTouchStart={ev => ev.stopPropagation()}
                            onClick={ev => ev.stopPropagation()}
                        >
                            <PermsOptions
                                quest={quest}
                                close={closePermsOptions}
                                showHeader={true}
                            />
                        </div>
                    </div>,
                    document.body,
                )}
        </>
    )
}

// A branch inside a direct message has no stream to open and, for someone
// invited into it, no parent to show, so it gets its own strip.
const ThinParent: React.FC<ThinParentProps> = props =>
    props.quest && isDirectMessageBranch(props.quest) ? (
        // In the open thread the place bar names the person and the posts
        // above (PanelPlaceBar DirectMessagePlace), like a channel thread's.
        props.redundantWithHeader ? null : (
            <DirectMessageBranchCrumb
                quest={props.quest}
                onHoverChange={props.onHoverChange}
                onRowClick={props.onRowClick}
            />
        )
    ) : (
        <StreamThinParent {...props} />
    )

export default ThinParent
